import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, AlertTriangle, ArrowRight, Clock, TrendingUp, ClipboardCheck, FileText, UserPlus, Plus, BookOpen, Target } from 'lucide-react';
import { getModuleIcon, MODULES, getFamiliesForModule, getAllowedTiersForGroup, humanizeFamilyId } from '../../../content';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { PageLayout } from '../../shared/PageLayout';
import { EmptyState } from '../../../components/shared/EmptyState';
import { StudentData } from './StudentDetailPanel';
import { DjayAvatar } from '../../../mascot';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
import { getAvatarGradient } from '../utils/avatarUtils';
import { needsAttention, STRUGGLING_THRESHOLD } from '../utils/studentUtils';
import { getFamilyMasteryKey } from '../../../mastery';
import { getStudentInsights, DifferentiationInsight } from '../utils/differentiationInsights';
import { GroupId } from '../../../config/systemConfig';
import { formatRelativeTime } from '../../../utils/formatters';
import { containerVariants, itemVariants, listContainerVariants, listItemVariants } from '../shared/animationVariants';
import { CreatePracticePanel } from '../components/CreatePracticePanel';
/** Days of inactivity before a student is flagged as "long inactive" (MN-02) */
const LONG_INACTIVE_DAYS = 14;
const LONG_INACTIVE_MS = LONG_INACTIVE_DAYS * 24 * 60 * 60 * 1000;
// Simple animated number component (SC-3: added cancelAnimationFrame cleanup)
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    let startTime: number;
    let rafId: number;
    const duration = 1000; // 1 second
    const startValue = 0;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutQuart
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(startValue + easeProgress * (value - startValue)));
      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        setDisplayValue(value);
      }
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [value]);
  return <>{displayValue}</>;
}
interface TodayPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
  onNavigate: (route: string) => void;
  classroomName?: string;
}
export function TodayPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent,
  onNavigate,
  classroomName
}: TodayPageProps) {
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [isCreatePracticeOpen, setIsCreatePracticeOpen] = useState(false);
  const stats = useMemo(() => {
    if (students.length === 0) return null;
    const strugglingStudents: Array<{
      student: StudentData;
      insight: DifferentiationInsight;
    }> = [];
    let studentsPracticedThisWeek = 0;
    let totalMasterySum = 0;
    let totalMasteryCount = 0;
    const moduleMasterySums: Record<string, {
      sum: number;
      count: number;
    }> = {};
    MODULES.forEach(m => {
      moduleMasterySums[m.id] = {
        sum: 0,
        count: 0
      };
    });
    const allowedTiers = getAllowedTiersForGroup(selectedGroup);
    const familyStatsMap: Record<string, {
      familyId: string;
      moduleId: string;
      label: string;
      totalMastery: number;
      practicedCount: number;
      strugglingCount: number;
    }> = {};
    const inactiveStudents = students.filter(student => {
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      return weeklyActivity.filter(d => d.practiced).length === 0;
    }).map(student => {
      let lastActive = 0;
      if (student.data.questionStates) {
        for (const state of Object.values(student.data.questionStates)) {
          if (state.lastAttemptDate > lastActive) {
            lastActive = state.lastAttemptDate;
          }
        }
      }
      return {
        student,
        lastActive
      };
    }).sort((a, b) => b.lastActive - a.lastActive);
    // Group context is needed for insights, we can extract it from the first student's data if available
    // or fallback to 6. In a real app, selectedGroup would be passed as a prop.
    // Since TodayPage doesn't receive selectedGroup, we'll try to infer it or default to 6.
    // Actually, we can just pass 6 as a fallback, but let's try to get it from the first student's groupVersion if possible.
    // Wait, getStudentInsights needs a GroupId. Let's just use 6 as a fallback since we don't have it in props.
    // A better way: TodayPage is rendered inside TeacherShellV2 which has selectedGroup.
    // Let's add selectedGroup to TodayPageProps.
    students.forEach(student => {
      let hasPracticed = false;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          hasPracticed = true;
          totalMasterySum += mastery;
          totalMasteryCount++;
          moduleMasterySums[module.id].sum += mastery;
          moduleMasterySums[module.id].count++;
        }
      });
      if (hasPracticed) {
        // Use the selectedGroup from props
        const insights = getStudentInsights(student.data, selectedGroup, 1);
        if (insights.length > 0 && insights[0].mastery < STRUGGLING_THRESHOLD) {
          strugglingStudents.push({
            student,
            insight: insights[0]
          });
        }
      }
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      if (weeklyActivity.some(d => d.practiced)) {
        studentsPracticedThisWeek++;
      }
      // Calculate family stats for class-wide weak spots
      MODULES.forEach(module => {
        const families = getFamiliesForModule(module.id);
        const relevantFamilies = families.filter(f => allowedTiers.has(f.tier));
        relevantFamilies.forEach(f => {
          const key = getFamilyMasteryKey(f.familyId, selectedGroup);
          const fm = student.data.familyMastery?.[key];
          if (fm && fm.attempts > 0) {
            if (!familyStatsMap[f.familyId]) {
              familyStatsMap[f.familyId] = {
                familyId: f.familyId,
                moduleId: module.id,
                label: humanizeFamilyId(f.familyId),
                totalMastery: 0,
                practicedCount: 0,
                strugglingCount: 0
              };
            }
            familyStatsMap[f.familyId].practicedCount++;
            familyStatsMap[f.familyId].totalMastery += fm.masteryScore;
            if (fm.masteryScore < STRUGGLING_THRESHOLD) {
              familyStatsMap[f.familyId].strugglingCount++;
            }
          }
        });
      });
    });
    const classWideWeakSpots = Object.values(familyStatsMap).map(f => ({
      ...f,
      avgMastery: Math.round(f.totalMastery / f.practicedCount)
    })).filter(f => f.strugglingCount >= 2) // Only show if multiple students struggle
    .sort((a, b) => {
      if (b.strugglingCount !== a.strugglingCount) {
        return b.strugglingCount - a.strugglingCount;
      }
      return a.avgMastery - b.avgMastery;
    }).slice(0, 5);
    const classAverageMastery = totalMasteryCount > 0 ? Math.round(totalMasterySum / totalMasteryCount) : 0;
    let strongestModule = MODULES[0];
    let weakestClassModule = MODULES[0];
    let highestAvg = -1;
    let lowestAvg = 101;
    MODULES.forEach(m => {
      const mStats = moduleMasterySums[m.id];
      if (mStats.count > 0) {
        const avg = mStats.sum / mStats.count;
        if (avg > highestAvg) {
          highestAvg = avg;
          strongestModule = m;
        }
        if (avg < lowestAvg) {
          lowestAvg = avg;
          weakestClassModule = m;
        }
      }
    });
    if (highestAvg === -1) {
      strongestModule = MODULES[0];
      weakestClassModule = MODULES[0];
    }
    return {
      strugglingStudents: strugglingStudents.sort((a, b) => a.insight.mastery - b.insight.mastery).slice(0, 6),
      totalStruggling: strugglingStudents.length,
      inactiveStudents,
      studentsPracticedThisWeek,
      classAverageMastery,
      strongestModule,
      weakestClassModule,
      highestAvg,
      lowestAvg,
      classWideWeakSpots
    };
  }, [students, getModuleMastery, selectedGroup]);
  const hour = new Date().getHours();
  let greeting = 'Goedenavond';
  let greetingIcon = '🌙';
  if (hour >= 6 && hour < 12) {
    greeting = 'Goedemorgen';
    greetingIcon = '☀️';
  } else if (hour >= 12 && hour < 18) {
    greeting = 'Goedemiddag';
    greetingIcon = '🌤️';
  }
  const dateString = new Date().toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  if (students.length === 0) {
    return <PageLayout title="Vandaag" description="Overzicht van de klas" actions={<Button onClick={() => setIsAddStudentOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-7635">
            <Plus className="w-4 h-4" data-id="element-7636" />
            Leerling Toevoegen
          </Button>} data-id="element-7634">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-7637">
          <EmptyState title="Nog geen leerlingen" message="Je klas is nog leeg. Voeg leerlingen toe om hun voortgang te volgen en inzicht te krijgen in hun prestaties." emotion="thinking" actionLabel="Naar Mijn Klas" onAction={() => onNavigate('class')} colorScheme="indigo" data-id="element-7638" />
        </motion.div>
      </PageLayout>;
  }
  if (!stats) return null;
  const StrongestIcon = getModuleIcon(stats.strongestModule.icon);
  const WeakestIcon = getModuleIcon(stats.weakestClassModule.icon);
  return <PageLayout title="Vandaag" description={classroomName ? `Actuele inzichten voor ${classroomName}` : 'Actuele inzichten en aandachtspunten'} data-id="element-7639">
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-7640">
        {/* Welcome Block */}
        <motion.div variants={itemVariants} className="bg-indigo-600 rounded-2xl p-6 text-white shadow-md relative overflow-hidden" data-id="element-7641">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-[0.04] rounded-full -translate-y-1/2 translate-x-1/3 blur-2xl" data-id="element-7642"></div>
          <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-7643">
            <div data-id="element-7644">
              <h1 className="text-3xl font-black mb-1" data-id="element-7645">
                {greetingIcon} {greeting}!
              </h1>
              <p className="text-indigo-100 font-medium text-lg" data-id="element-7646">
                {classroomName || 'Mijn Klas'} •{' '}
                <span className="capitalize" data-id="element-7647">{dateString}</span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm font-medium bg-white/10 px-4 py-2.5 rounded-xl backdrop-blur-sm border border-white/10" data-id="element-7648">
              <div className="flex items-center gap-1.5" data-id="element-7649">
                <Users className="w-4 h-4 text-indigo-200" data-id="element-7650" />
                <span data-id="element-7651">{students.length} leerlingen</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-7652"></div>
              <div className="flex items-center gap-1.5" data-id="element-7653">
                <TrendingUp className="w-4 h-4 text-emerald-300" data-id="element-7654" />
                <span data-id="element-7655">{stats.studentsPracticedThisWeek} actief</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-7656"></div>
              <div className="flex items-center gap-1.5" data-id="element-7657">
                <AlertTriangle className={`w-4 h-4 ${stats.totalStruggling > 0 ? 'text-rose-300' : 'text-indigo-200'}`} data-id="element-7658" />
                <span data-id="element-7659">{stats.totalStruggling} aandachtspunten</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions Row */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-7660">
          <button onClick={() => setIsCreatePracticeOpen(true)} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-7661">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-7662">
              <BookOpen className="w-5 h-5" data-id="element-7663" />
            </div>
            <div data-id="element-7664">
              <div className="font-bold text-slate-900 group-hover:text-blue-700 transition-colors" data-id="element-7665">
                Huiswerk
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-7666">
                Oefenen opgeven
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('assessments')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-7667">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors" data-id="element-7668">
              <ClipboardCheck className="w-5 h-5" data-id="element-7669" />
            </div>
            <div data-id="element-7670">
              <div className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors" data-id="element-7671">
                Nieuwe toets
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-7672">
                Toets inplannen
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('reports')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-7673">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors" data-id="element-7674">
              <FileText className="w-5 h-5" data-id="element-7675" />
            </div>
            <div data-id="element-7676">
              <div className="font-bold text-slate-900 group-hover:text-emerald-700 transition-colors" data-id="element-7677">
                Rapporten
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-7678">
                Resultaten bekijken
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('management')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-7679">
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center group-hover:bg-amber-600 group-hover:text-white transition-colors" data-id="element-7680">
              <UserPlus className="w-5 h-5" data-id="element-7681" />
            </div>
            <div data-id="element-7682">
              <div className="font-bold text-slate-900 group-hover:text-amber-700 transition-colors" data-id="element-7683">
                Leerling toevoegen
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-7684">
                Klas beheren
              </div>
            </div>
          </button>
        </motion.div>

        {/* Class Overview Card */}
        <motion.div variants={itemVariants} data-id="element-7685">
          <Card className="bg-white border-slate-200 shadow-sm overflow-hidden p-5" data-id="element-7686">
            <div className="flex items-center gap-2 mb-4" data-id="element-7687">
              <TrendingUp className="w-5 h-5 text-indigo-600" data-id="element-7688" />
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider" data-id="element-7689">
                Klas Overzicht
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-7690">
              <div className="flex flex-col" data-id="element-7691">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-7692">
                  Gemiddelde beheersing
                </div>
                <div className="flex items-end gap-2" data-id="element-7693">
                  <div className="text-3xl font-black text-slate-900" data-id="element-7694">
                    <AnimatedNumber value={stats.classAverageMastery} data-id="element-7695" />%
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0" data-id="element-7696">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-7697">
                  Sterkste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-7698">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center" data-id="element-7699">
                    <StrongestIcon className="w-4 h-4" data-id="element-7700" />
                  </div>
                  <div data-id="element-7701">
                    <div className="font-bold text-slate-900" data-id="element-7702">
                      {stats.strongestModule.title}
                    </div>
                    <div className="text-xs font-medium text-emerald-600" data-id="element-7703">
                      {stats.highestAvg > -1 ? Math.round(stats.highestAvg) : 0}
                      % beheersing
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0 cursor-pointer hover:bg-slate-50 rounded-xl p-2 -m-2 transition-colors" onClick={() => onNavigate('subjects')} data-id="element-7704">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-7705">
                  Zwakste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-7706">
                  <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center" data-id="element-7707">
                    <WeakestIcon className="w-4 h-4" data-id="element-7708" />
                  </div>
                  <div data-id="element-7709">
                    <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-7710">
                      {stats.weakestClassModule.title}
                    </div>
                    <div className="text-xs font-medium text-rose-600" data-id="element-7711">
                      {stats.lowestAvg < 101 ? Math.round(stats.lowestAvg) : 0}%
                      beheersing
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Class-wide Weak Spots */}
        {stats.classWideWeakSpots.length > 0 && <motion.section variants={itemVariants} data-id="element-7712">
            <div className="flex items-center justify-between mb-4" data-id="element-7713">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-7714">
                <Target className="w-4 h-4 text-indigo-500" data-id="element-7715" />
                Klas-brede Zwakke Plekken
              </h2>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-7716">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-7717">
                <div className="w-8" data-id="element-7718"></div>
                <div data-id="element-7719">Onderdeel</div>
                <div className="text-right w-32" data-id="element-7720">Moeite mee</div>
                <div className="w-6" data-id="element-7721"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-7722">
                {stats.classWideWeakSpots.map((spot, i) => {
              const moduleDef = MODULES.find(m => m.id === spot.moduleId);
              const IconComp = getModuleIcon(moduleDef?.icon || 'calculator');
              return <motion.div key={spot.familyId} variants={listItemVariants} onClick={() => onNavigate('subjects')} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-7723">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${moduleDef?.bgColor || 'bg-slate-100'} ${moduleDef?.color || 'text-slate-500'}`} data-id="element-7724">
                        <IconComp className="w-4 h-4" data-id="element-7725" />
                      </div>
                      <div className="min-w-0 flex flex-col gap-1" data-id="element-7726">
                        <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-7727">
                          {spot.label}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-7728">
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-7729">
                            <div className={`h-full rounded-full ${spot.avgMastery >= 75 ? 'bg-emerald-500' : spot.avgMastery >= 40 ? 'bg-indigo-400' : 'bg-rose-500'}`} style={{
                        width: `${Math.max(2, spot.avgMastery)}%`
                      }} data-id="element-7730" />
                          </div>
                          <span className="text-[10px] font-bold text-slate-400" data-id="element-7731">
                            {spot.avgMastery}% gem.
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end w-32" data-id="element-7732">
                        <span className="font-bold text-xs text-rose-600 bg-rose-50 px-2 py-1 rounded-md" data-id="element-7733">
                          {spot.strugglingCount} van {spot.practicedCount} lln
                        </span>
                      </div>
                      <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-7734">
                        <ArrowRight className="w-4 h-4" data-id="element-7735" />
                      </div>
                    </motion.div>;
            })}
              </motion.div>
            </Card>
          </motion.section>}

        {/* Attention Needed */}
        <motion.section variants={itemVariants} data-id="element-7736">
          <div className="flex items-center justify-between mb-4" data-id="element-7737">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-7738">
              <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-7739" />
              Aandachtspunten
            </h2>
          </div>

          {stats.strugglingStudents.length > 0 ? <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-7740">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-7741">
                <div className="w-8" data-id="element-7742"></div>
                <div data-id="element-7743">Naam & Vak</div>
                <div className="text-right w-24" data-id="element-7744">Beheersing</div>
                <div className="w-6" data-id="element-7745"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-7746">
                {stats.strugglingStudents.map((item, i) => {
              const moduleDef = MODULES.find(m => m.id === item.insight.moduleId);
              const IconComp = getModuleIcon(moduleDef?.icon || 'calculator');
              return <motion.div key={item.student.id} variants={listItemVariants} onClick={() => onSelectStudent(item.student.id)} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-7747">
                      <div className="relative" data-id="element-7748">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(item.student.name)} flex items-center justify-center text-white font-bold text-sm shadow-sm`} data-id="element-7749">
                          {item.student.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-rose-500 shadow-sm" role="img" aria-label="Aandacht nodig" data-id="element-7750" />
                      </div>
                      <div className="min-w-0 flex flex-col gap-1" data-id="element-7751">
                        <div className="flex items-center gap-2" data-id="element-7752">
                          <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-7753">
                            {item.student.name}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-7754">
                            <IconComp className="w-3 h-3 text-rose-500" data-id="element-7755" />
                            <span className="truncate font-medium" data-id="element-7756">
                              {item.insight.familyName}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 truncate" data-id="element-7757">
                          {item.insight.recommendation}
                        </div>
                      </div>
                      <div className="flex items-center justify-end w-24" data-id="element-7758">
                        <span className="font-black text-sm text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md" data-id="element-7759">
                          {item.insight.mastery}%
                        </span>
                      </div>
                      <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-7760">
                        <ArrowRight className="w-4 h-4" data-id="element-7761" />
                      </div>
                    </motion.div>;
            })}
              </motion.div>
            </Card> : <Card className="p-8 text-center border-emerald-100 bg-emerald-50/50 shadow-sm overflow-hidden relative" data-id="element-7762">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(52,211,153,0.1)_0,transparent_100%)]" data-id="element-7763" />
              <motion.div className="flex justify-center mb-4 relative z-10" initial={{
            scale: 0.8,
            y: 20
          }} animate={{
            scale: 1,
            y: 0
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20,
            delay: 0.2
          }} data-id="element-7764">
                <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-7765">
                  <DjayAvatar size="md" emotion="happy" data-id="element-7766" />
                </motion.div>
              </motion.div>
              <h3 className="text-xl font-black text-emerald-900 mb-2 relative z-10" data-id="element-7767">
                Alles onder controle!
              </h3>
              <p className="text-emerald-700 font-medium relative z-10" data-id="element-7768">
                Er zijn momenteel geen leerlingen die structureel vastlopen.
                Goed bezig! 🎉
              </p>
            </Card>}
          {stats.totalStruggling > 6 && <div className="mt-4 text-center" data-id="element-7769">
              <button onClick={() => onNavigate('class')} className="text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors inline-flex items-center gap-1.5 group" data-id="element-7770">
                Bekijk alle {stats.totalStruggling} leerlingen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-7771" />
              </button>
            </div>}
        </motion.section>

        {/* Inactive Students */}
        {stats.inactiveStudents.length > 0 && <motion.section variants={itemVariants} data-id="element-7772">
            <div className="flex items-center justify-between mb-4" data-id="element-7773">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-7774">
                <Clock className="w-4 h-4 text-amber-500" data-id="element-7775" />
                Niet geoefend deze week
              </h2>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-7776">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-7777">
                <div className="w-8" data-id="element-7778"></div>
                <div data-id="element-7779">Naam</div>
                <div className="text-right w-32" data-id="element-7780">Inactief sinds</div>
                <div className="w-6" data-id="element-7781"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-7782">
                {stats.inactiveStudents.slice(0, 5).map(({
              student,
              lastActive
            }, i) => {
              const inactiveLabel = lastActive > 0 ? formatRelativeTime(lastActive) : 'Nog nooit geoefend';
              return <motion.div key={student.id} variants={listItemVariants} onClick={() => onSelectStudent(student.id)} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-7783">
                        <div className="relative" data-id="element-7784">
                          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(student.name)} flex items-center justify-center text-white font-bold text-sm shadow-sm`} data-id="element-7785">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-amber-500 shadow-sm" role="img" aria-label="Inactief deze week" data-id="element-7786" />
                        </div>
                        <div className="min-w-0" data-id="element-7787">
                          <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-7788">
                            {student.name}
                          </div>
                        </div>
                        <div className="flex items-center justify-end w-32" data-id="element-7789">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-md ${lastActive === 0 ? 'bg-rose-50 text-rose-600' : Date.now() - lastActive > LONG_INACTIVE_MS ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-7790">
                            {inactiveLabel}
                          </span>
                        </div>
                        <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-7791">
                          <ArrowRight className="w-4 h-4" data-id="element-7792" />
                        </div>
                      </motion.div>;
            })}
              </motion.div>
              {stats.inactiveStudents.length > 5 && <div className="p-3 bg-slate-50 border-t border-slate-100 text-center" data-id="element-7793">
                  <button onClick={() => onNavigate('class')} className="text-xs font-bold text-slate-500 hover:text-indigo-600 uppercase tracking-wider transition-colors inline-flex items-center gap-1 group" data-id="element-7794">
                    + {stats.inactiveStudents.length - 5} meer bekijken
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" data-id="element-7795" />
                  </button>
                </div>}
            </Card>
          </motion.section>}
      </motion.div>
      <CreatePracticePanel open={isCreatePracticeOpen} onClose={() => setIsCreatePracticeOpen(false)} students={students} selectedGroup={selectedGroup} teacherId="teacher" // In a real app, this would come from context/props
    classroomId={null} // In a real app, this would come from context/props
    onSuccess={() => {
      // Handle success if needed
    }} data-id="element-7796" />
    </PageLayout>;
}